import React, { FC } from 'react'
import classNames from 'classnames'
import { BellOutlineIcon } from '@/atoms/Icons/BellOutlineIcon'
import { EarlyAccessIcon } from '@/atoms/Icons/EarlyAccessIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionXS, TitleXS } from '@/atoms/Text'
import {
  buildCloudinaryImageUrlFromContentfulObject,
  formatContentfulObjectFromCloudinaryUrl,
} from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { TicketsLandingRelease } from './types'

export interface Props {
  release: TicketsLandingRelease
}

const ReleaseStatusIcon: FC<{ releaseStatus: string }> = ({ releaseStatus }) => {
  switch (releaseStatus) {
    case 'past-release':
      return <EarlyAccessIcon color={'black'} size={14} />
    case 'in-theaters':
      return <TicketIcon color={'black'} size={14} />
    case 'upcoming':
      return <BellOutlineIcon color={'black'} size={14} />
    default:
      return null
  }
}

export const LandingReleaseCard: FC<Props> = ({ release }) => {
  if (!release.linkUrl) return <InternalLandingReleaseCard release={release} />
  return (
    <InternalLink href={`${release.linkUrl}?utm_medium=web&utm_source=${release.releaseStatus}`}>
      <InternalLandingReleaseCard release={release} />
    </InternalLink>
  )
}

function mapReleaseStatusToPillTitle(value: string, t: TranslateFunction) {
  switch (value) {
    case 'in-theaters':
      return t('getShowTimes', 'Get Showtimes')
    case 'past-release':
      return t('watchNow', 'Watch Now')
    case 'upcoming':
      return t('learnMore', 'Learn More')
    default:
      return value
  }
}

export const InternalLandingReleaseCard: React.FC<Props> = ({ release }) => {
  const track = useSafeTrack()

  const { t } = useTranslate('tickets')

  const canShowTBA = !release.releaseDay && !release.releaseMonth
  const pillTitle = mapReleaseStatusToPillTitle(release.releaseStatus, t)

  return (
    <div
      key={release.name}
      className="mb-4 flex min-w-[215px] flex-row gap-2 text-left"
      onClick={() =>
        track('Tickets Landing Card Clicked', {
          releaseStatus: release.releaseStatus,
          release: release.name,
        })
      }
    >
      <div className="flex w-[50px] flex-col justify-center py-3 text-center">
        {canShowTBA ? (
          <div className={classNames('z-10 py-4', 'bg-core-gray-950')}>
            <div className="text-[20px] font-semibold uppercase">{t('toBeAnnounced', 'TBA')}</div>
            <div className="text-[13px] font-semibold">{release.releaseYear}</div>
          </div>
        ) : (
          <div className={classNames('z-10 py-4', 'bg-core-gray-950')}>
            <div className="text-[13px] font-semibold uppercase">{release.releaseMonth}</div>
            <div className="text-[32px] font-semibold uppercase leading-9">{release.releaseDay}</div>
            <div className="text-[13px] font-semibold">{release.releaseYear}</div>
          </div>
        )}
      </div>
      <div className="flex w-full flex-row overflow-hidden rounded-xl">
        <Image
          src={buildCloudinaryImageUrlFromContentfulObject(release.verticalPoster, 'h_134,w_90,c_fill')}
          alt={release.name}
          height={134}
          width={90}
          className="shrink-0 grow-0"
        />
        <div className="relative flex grow">
          <ResponsiveBackgroundImage
            src={
              release.verticalPoster
                ? buildCloudinaryImageUrlFromContentfulObject(release.verticalPoster)
                : buildCloudinaryImageUrlFromContentfulObject(
                    formatContentfulObjectFromCloudinaryUrl('v1694104235/Show.png'),
                  )
            }
            className="h-full w-full bg-cover bg-center bg-no-repeat"
          />
          <div className="flex h-full w-full flex-col bg-white/[0.015] px-3 py-[15px] backdrop-blur-3xl">
            <TitleXS className="mb-1 w-full">{release.name}</TitleXS>
            <CaptionXS className="line-clamp-2 w-full font-light">{release.description}</CaptionXS>
            {release.linkUrl ? (
              <div className="mt-4 flex w-fit flex-row items-center gap-1 overflow-hidden rounded-md bg-white px-2.5 py-1 text-[10px]">
                <>
                  <ReleaseStatusIcon releaseStatus={release.releaseStatus} />{' '}
                  <span className="mr-0.5 whitespace-nowrap pt-[1px] font-medium text-black">{pillTitle}</span>
                </>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
